/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import analytics from '../utils/analytics';

const ReviewFailedTest = ({ user, testId, onBackToTests }) => {
  const [failedQuestions, setFailedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFailedQuestions = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tests/${testId}/review/${user.id}`);
        if (!response.ok) {
          throw new Error('Failed to fetch failed questions');
        }
        const data = await response.json();
        setFailedQuestions(data);
        
        // Track failed question review initiation
        if (data.length > 0) {
          analytics.trackEvent('failed_questions_review_start', {
            category: 'education',
            test_id: testId,
            questions_count: data.length,
            label: 'review_session_start'
          });
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching failed questions:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchFailedQuestions();
  }, [testId, user.id]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <Card className="w-full max-w-4xl mx-auto m-2 sm:m-4">
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="text-lg sm:text-2xl">Review Failed Questions</CardTitle>
        <CardDescription className="text-sm">Review the questions you got wrong in your last attempt</CardDescription>
      </CardHeader>
      <CardContent className="p-4 sm:p-6">
        {failedQuestions.map((question, index) => (
          <div key={index} className="mb-6 p-3 sm:p-4 border rounded-lg">
            <p className="font-bold mb-2 break-words">Question: {question.question_text}</p>
            {question.image_url && (
              <div className="mb-3">
                <img
                  src={question.image_url.startsWith('http') ? question.image_url : `${process.env.REACT_APP_API_URL}${question.image_url}`}
                  alt="Question illustration"
                  className="max-w-full h-auto rounded border max-h-[200px] sm:max-h-[300px]"
                  onError={(e) => {
                    console.error('Failed to load image:', question.image_url);
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            <p className="text-red-600 break-words">Your answer: {question.selected_answer}</p>
          </div>
        ))}
        <Button onClick={onBackToTests} className="w-full sm:w-auto min-h-[44px]">Back to Available Tests</Button>
      </CardContent>
    </Card>
  );
};

export default ReviewFailedTest;